import calendars from 'scoreboard/components/calendars'
import _ from 'lodash'
const { DAILY, WEEKLY, MONTHLY, YEARLY, FISCAL_YEARLY } = calendars

let yearly = {
	id: YEARLY,
	type: YEARLY,
	label: 'admin.calendar.yearly',
	avgPeriodSize: 365
}
let monthly = {
	id: MONTHLY,
	type: MONTHLY,
	label: 'admin.calendar.monthly',
	avgPeriodSize: 31
}
let weekly = {
	id: WEEKLY,
	type: WEEKLY,
	label: 'admin.calendar.weekly',
	avgPeriodSize: 7
}
let daily = {
	id: DAILY,
	type: DAILY,
	label: 'admin.calendar.daily',
	avgPeriodSize: 1
}
let calendarsByType = {
	[YEARLY]: yearly,
	[MONTHLY]: monthly,
	[WEEKLY]: weekly,
	[DAILY]: daily
}

/**
 * #38378 Sorts the calendars by their size, from largest to smallest. This is used
 * by the functions below to ensure the correct calendars are returned
 */
function getSortedCalendars(cals = calendars.calendars) {
	return _.reverse(_.sortBy(cals, ['avgPeriodSize']))
}

function validBreakdownCalendars(groupCalendarId, cals = calendars.calendars) {
	let groupCalendar = _.find(cals, { id: parseInt(groupCalendarId) }) ||
		calendarsByType[groupCalendarId]

	if (!groupCalendar) {
		return []
	}

	let breakdownCalendars = cals

	if (groupCalendar.id == YEARLY || groupCalendar.type == YEARLY ||
		groupCalendar.type == FISCAL_YEARLY) {
		breakdownCalendars = ensureCalendar(breakdownCalendars, monthly)
	}
	if (groupCalendar.id == MONTHLY || groupCalendar.type == WEEKLY) {
		breakdownCalendars = ensureCalendar(breakdownCalendars, daily)
	}
	breakdownCalendars = getSortedCalendars(breakdownCalendars)

	// #37600 if there is only 1 calendar, don't filter any out
	if (breakdownCalendars.length <= 1) {
		return breakdownCalendars
	}

	// Only allow breakdown calendars that are "smaller" than the range calendar
	return breakdownCalendars.filter(function (c) {
		return c.avgPeriodSize < groupCalendar.avgPeriodSize
	})
}

/**
 * #37600 return the possible calendar options for a group calendar dropdown
 *
 * @param {Calendar[]} [cals] - the set of defined calendars to start with.
 * Defaults to all the calendars defined in the database. Probably only
 * appropriate for unit testing
 *
 * @param {Boolean} [allowUndefinedCalendars=true] whether options like "Weekly"
 * should be available even if there is no Weekly calendar defined in the
 * database.
 */
function validGroupCalendars({ cals, allowUndefinedCalendars } = {}) {
	cals = cals ?? calendars.calendars
	allowUndefinedCalendars = allowUndefinedCalendars ?? true
	let groupCalendars = getSortedCalendars(cals)

	// #37600 Do not display the smallest calendar to ensure it cannot be the
	// same as the breakdown calendar
	let lastCalendar = _.last(groupCalendars)
	if (lastCalendar.type !== YEARLY && lastCalendar.type !== WEEKLY) {
		groupCalendars = groupCalendars.slice(0, groupCalendars.length - 1)
	}

	if (allowUndefinedCalendars) {
		// always need year and week here for "day of the week" and
		// "month of the year"
		groupCalendars = ensureCalendar(groupCalendars, yearly)
		groupCalendars = ensureCalendar(groupCalendars, weekly)
	}
	groupCalendars = getSortedCalendars(groupCalendars)
	return groupCalendars
}

function ensureCalendar(calendars, calendar) {
	let cals = _.clone(calendars)
	if (!_.find(cals, { type: calendar.type })) {
		cals.push(calendar)
	}
	return cals
}

/**
 * @return the largest calendar available
 */
function getLargestCalendar () {
	return getSortedCalendars()[0]
}

/**
 * @return {{ calendarId: number, subCalendarId: number }} the
 * first valid group and breakdown calendar IDs. i.e. The default selections
 * from a select populated using the above methods.
 */
function getFirstValidCalendarAndSubCalendarIds () {
	const calendarId = validGroupCalendars()[0].id
	const subCalendarId = validBreakdownCalendars(calendarId)[0].id
	return { calendarId, subCalendarId }
}

export {
	validBreakdownCalendars,
	validGroupCalendars,
	ensureCalendar,
	yearly,
	monthly,
	weekly,
	daily,
	getLargestCalendar,
	getFirstValidCalendarAndSubCalendarIds
}
